/**
 * "Can this customer get into the client portal" - the marker a register row wears.
 *
 * Draws for the two states that are worth opening the row for and nothing for
 * the third, the same shape `NextStepBadge` uses. Which states those are is the
 * opposite way round here, and on purpose: almost no client has portal access
 * yet, so access is the exception a list should point at. The day that inverts -
 * most customers in, a handful out - this rule should invert with it, because a
 * badge on every row marks nothing.
 *
 * ## Why the icon carries it alone
 *
 * Measured in the browser, not guessed: with the word in it this chip was 68px
 * wide (100px for the longer Estonian one) in a subline whose usable width is
 * 197px, of which the next-step chip already takes 140. The badge ran 27 to 59
 * pixels past the name column and was clipped mid-word - which reads as a
 * broken layout rather than as a marker.
 *
 * The legend is the portal menu directly above the list: the same two icons sit
 * beside the same two words there. A reader who wonders what the key means finds
 * the answer in the control they would use next anyway, and the hover title says
 * it too.
 */
import { Clock, KeyRound, MailX, Send } from "lucide-react";
import { useT } from "../../../i18n/I18nProvider";
import { portalState, type PortalAccess } from "../../../lib/portalAccess";
import { fmtDate, fmtDateTime } from "../../ui";

export function PortalBadge({ access, className }: { access: PortalAccess | null | undefined; className?: string }) {
  const t = useT();
  const state = portalState(access);
  if (state === "none") return null;

  const linked = state === "linked";
  const Icon = linked ? KeyRound : Send;
  const tone = linked
    ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400"
    : "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-400";

  // State first, then the count. The register's question is which rows to open;
  // "2 of 3 people" is a client-card question and belongs behind the pointer.
  const label = t(linked ? "clients.portal.linked" : "clients.portal.invited");
  const count = linked
    ? t("clients.portal.linkedCount", { n: access?.linked ?? 0 })
    : t("clients.portal.invitedCount", { n: access?.invited ?? 0 });

  return (
    <span
      className={`inline-flex shrink-0 items-center rounded-full border p-1 ${tone} ${className ?? ""}`}
      title={`${label} - ${count}`}
      aria-label={label}
    >
      <Icon className="size-2.5" />
    </span>
  );
}

/**
 * "Has this person ever actually opened the portal, and when" - the line under a
 * linked contact.
 *
 * Never-opened is drawn LOUDER than a recent visit, which is the opposite of the
 * usual rule and the whole reason this exists. Access granted and never used is
 * the likeliest outcome this feature has, and it is invisible unless something
 * says so: the green "portal" badge beside the name looks the same either way.
 *
 * The stamp is written by the portal's own scope resolver, so it means the
 * portal served them - not that they signed in to the platform somewhere else.
 */
export function PortalLastSeen({ lastSeenAt }: { lastSeenAt: string | null }) {
  const t = useT();
  if (!lastSeenAt) {
    return (
      <span className="inline-flex items-center gap-1 text-amber-700 dark:text-amber-400">
        <Clock className="size-3" />
        {t("clients.portal.neverSeen")}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1">
      <Clock className="size-3" />
      {t("clients.portal.lastSeen", { at: fmtDateTime(lastSeenAt) })}
    </span>
  );
}

/**
 * "We asked, and nobody came" - the line under a contact whose invitation ran
 * out unredeemed.
 *
 * It exists because the alternative is nothing at all. An invitation is pending
 * for seven days and then simply stops being pending, and until now the row went
 * back to looking exactly like a contact nobody had ever approached. Those are
 * different facts with different next moves: one is a job to do, the other is
 * evidence about whether the portal is wanted. In Perfex, where the same portal
 * stood open for seven years, 2 of 38 contacts ever signed in, and this is the
 * shape that number arrives in.
 *
 * Drawn in the same amber as a never-opened account rather than in red: a client
 * who ignored an invitation has not done anything wrong, and the operator's next
 * move is a conversation, not an alarm.
 */
export function PortalInviteLapsed({ at }: { at: string | null | undefined }) {
  const t = useT();
  if (!at) return null;
  return (
    <span className="inline-flex items-center gap-1 text-amber-700 dark:text-amber-400">
      <MailX className="size-3" />
      {t("clients.portal.inviteLapsed", { at: fmtDate(at) })}
    </span>
  );
}
